import { themeSamples } from "@/themeSamples";
import { ArrowUpRight, Terminal } from "lucide-react";

const SERIF_ITALIC = { fontStyle: "italic", fontWeight: 400 };

function DirectionMock({ d, index }) {
  const tk = d.tokens;
  const radius = d.behavior.radius === "full" ? "999px" : d.behavior.radius;
  const display = { fontFamily: `'${d.fonts.display}', serif` };
  const body = { fontFamily: `'${d.fonts.body}', sans-serif` };
  const hairline = `1px solid ${tk.border}`;
  const isSerif = d.fonts.display === "Instrument Serif";

  const swatches = [
    ["Canvas", tk.bg],
    ["Ink", tk.text],
    ["Brand", tk.brand],
    ["Accent", tk.accent],
    ["Rule", tk.border],
  ];

  const stats = [
    ["06", "Training Tracks"],
    ["02", "Countries Served"],
    ["01", "Mission — ICT For Every School"],
  ];

  return (
    <section
      data-testid={`sample-direction-${index}`}
      className="overflow-hidden"
      style={{ background: tk.bg, border: hairline, ...body }}
    >
      {/* mini nav */}
      <div
        className="flex items-center justify-between px-8 py-5 md:px-14"
        style={{ borderBottom: hairline }}
      >
        <div className="flex items-center gap-2.5" style={{ color: tk.text }}>
          <Terminal size={16} style={{ color: tk.brand }} />
          <span className="text-[15px] font-bold tracking-tight" style={display}>
            The Geeks Labs
          </span>
        </div>
        <div className="hidden items-center gap-8 md:flex">
          {["About", "Tracks", "The Lab"].map((l) => (
            <span
              key={l}
              className="text-[11px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: tk.textMuted }}
            >
              {l}
            </span>
          ))}
          <span
            className="flex items-center text-[10px] font-bold uppercase tracking-[0.2em]"
            style={{ border: hairline, borderRadius: radius }}
          >
            <span className="px-2.5 py-1.5" style={{ color: tk.textMuted }}>EN</span>
            <span
              className="px-2.5 py-1.5"
              style={{ background: tk.accent, color: "#fff", borderRadius: radius }}
            >
              FR
            </span>
          </span>
          <span
            className="px-5 py-2 text-[11px] font-bold uppercase tracking-[0.15em]"
            style={{
              background: tk.btnPrimaryBg,
              color: tk.btnPrimaryText,
              borderRadius: radius,
            }}
          >
            Partner With Us
          </span>
        </div>
      </div>

      {/* hero */}
      <div className="px-8 pb-14 pt-16 md:px-14 md:pt-20">
        <div className="flex items-center gap-3">
          <span className="h-1.5 w-1.5" style={{ background: tk.brand, borderRadius: radius }} />
          <p
            className="text-[11px] font-bold uppercase tracking-[0.28em]"
            style={{ color: tk.brand }}
          >
            Tema, Ghana — Abidjan, Côte d'Ivoire
          </p>
        </div>
        <h2
          className="mt-8 max-w-4xl leading-[1.02] tracking-tight"
          style={{
            color: tk.text,
            fontSize: "clamp(2.6rem, 5.5vw, 4.4rem)",
            fontWeight: isSerif ? 400 : 800,
            ...display,
          }}
        >
          We build digital labs{" "}
          <span style={isSerif ? { ...SERIF_ITALIC, color: tk.brand } : { color: tk.brand }}>
            for every school
          </span>{" "}
          in West Africa.
        </h2>
        <div className="mt-8 grid gap-10 md:grid-cols-[1fr_auto] md:items-end">
          <p className="max-w-lg text-[15px] leading-relaxed" style={{ color: tk.textMuted }}>
            The Geeks Labs makes ICT an integral part of the school program — setting up
            computer labs and delivering hands-on IT training for students across Ghana and
            Côte d'Ivoire.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              className="px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.15em]"
              style={{
                background: tk.btnPrimaryBg,
                color: tk.btnPrimaryText,
                borderRadius: radius,
              }}
            >
              Explore Tracks
            </button>
            <button
              className="border px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.15em]"
              style={{
                borderColor: tk.btnSecondaryBorder,
                color: tk.btnSecondaryText,
                borderRadius: radius,
              }}
            >
              Start A Project
            </button>
          </div>
        </div>
      </div>

      {/* stats hairline grid */}
      <div
        className="grid grid-cols-3 px-8 md:px-14"
        style={{ borderTop: hairline, borderBottom: hairline }}
      >
        {stats.map(([v, l], i) => (
          <div
            key={l}
            className="py-7 pr-6"
            style={i > 0 ? { borderLeft: hairline, paddingLeft: "1.5rem" } : {}}
          >
            <div
              className="text-3xl md:text-4xl"
              style={{ color: tk.brand, fontWeight: isSerif ? 400 : 800, ...display }}
            >
              {v}
            </div>
            <div
              className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: tk.textMuted }}
            >
              {l}
            </div>
          </div>
        ))}
      </div>

      {/* service row + form mock */}
      <div className="grid md:grid-cols-2">
        <div className="px-8 py-10 md:px-14" style={{ borderRight: hairline }}>
          <div className="flex items-baseline justify-between">
            <span
              className="text-[11px] font-bold uppercase tracking-[0.25em]"
              style={{ color: tk.textMuted }}
            >
              01 / Tracks
            </span>
            <ArrowUpRight size={16} style={{ color: tk.brand }} />
          </div>
          <h3
            className="mt-6 text-2xl md:text-[1.7rem]"
            style={{ color: tk.text, fontWeight: isSerif ? 400 : 700, ...display }}
          >
            IT Infrastructure
          </h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed" style={{ color: tk.textMuted }}>
            Build, sustain and manage technical infrastructure — networks, servers and the
            hardware that powers modern schools.
          </p>
          <span
            className="mt-6 inline-block px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{
              color: tk.accent,
              border: `1px solid ${tk.accent}`,
              borderRadius: radius,
            }}
          >
            Cyber Security Included
          </span>
        </div>
        <div className="px-8 py-10 md:px-14" style={{ background: tk.surface }}>
          <span
            className="text-[11px] font-bold uppercase tracking-[0.25em]"
            style={{ color: tk.textMuted }}
          >
            02 / Contact
          </span>
          <div className="mt-6 grid grid-cols-2 gap-6">
            {["First Name *", "Email *"].map((l) => (
              <div key={l}>
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.2em]"
                  style={{ color: tk.textMuted }}
                >
                  {l}
                </div>
                <div className="mt-2 h-8" style={{ borderBottom: `1px solid ${tk.textMuted}` }} />
              </div>
            ))}
          </div>
          <button
            className="mt-7 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.15em]"
            style={{
              background: tk.btnPrimaryBg,
              color: tk.btnPrimaryText,
              borderRadius: radius,
            }}
          >
            Send Enquiry
          </button>
        </div>
      </div>

      {/* footer strip */}
      <div
        className="flex flex-wrap items-center justify-between gap-4 px-8 py-5 md:px-14"
        style={{ borderTop: hairline }}
      >
        <span className="text-[11px]" style={{ color: tk.textMuted }}>
          © 2026 The Geeks Labs — All rights reserved.
        </span>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {swatches.map(([label, hex]) => (
            <span key={label} className="flex items-center gap-1.5">
              <span
                className="h-3.5 w-3.5"
                style={{ background: hex, border: hairline, borderRadius: radius }}
              />
              <span className="text-[10px]" style={{ color: tk.textMuted }}>
                {label} {hex}
              </span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Samples() {
  return (
    <main data-testid="samples-page" className="min-h-screen bg-[#ECEEEF] px-4 py-14 md:px-10">
      {themeSamples.map((d) => (
        <link key={d.fonts.display} rel="stylesheet" href={d.fonts.importUrl} />
      ))}
      <div className="mx-auto max-w-[1240px]">
        <div className="flex items-end justify-between border-b-2 border-neutral-900 pb-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-500">
              The Geeks Labs — Design Directions
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-neutral-900 md:text-5xl">
              Three world-class light themes.
            </h1>
          </div>
          <p className="hidden max-w-xs text-right text-xs leading-relaxed text-neutral-500 md:block">
            Each direction is a complete system — color, typography, radius, rules and accent
            behavior. Reply with 1, 2 or 3 and the entire site is rebuilt in it.
          </p>
        </div>

        <div className="mt-14 flex flex-col gap-20">
          {themeSamples.map((d, i) => (
            <div key={d.name}>
              <div className="mb-6 flex flex-wrap items-baseline gap-x-5 gap-y-2">
                <span className="text-4xl font-extrabold tracking-tight text-neutral-300">
                  0{i + 1}
                </span>
                <h2 className="text-2xl font-bold tracking-tight text-neutral-900">{d.name}</h2>
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
                  {d.fonts.display} × {d.fonts.body}
                </span>
              </div>
              <p className="mb-6 max-w-3xl text-sm leading-relaxed text-neutral-600">
                {d.rationale}
              </p>
              <DirectionMock d={d} index={i} />
              <p className="mt-3 text-[11px] leading-relaxed text-neutral-500">
                <span className="font-bold text-neutral-700">Behavior —</span>{" "}
                {d.behavior.buttonStyle} {d.behavior.rules} {d.behavior.accentUsage}
              </p>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
